import React, { useState, useEffect, useMemo } from "react";
import T from '../../components/common/T';
import { useWorkforce } from "../../context/WorkforceContext";
import { useProjects } from "../../context/ProjectContext";
import { useNavigate } from "react-router-dom";
import WorkforceKanbanView from "./WorkforceKanbanView";
import DataTable from "../../components/common/DataTables.jsx";
import { 
    UserGroupIcon, BriefcaseIcon, TrashIcon, XMarkIcon, 
    MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon,
    PencilSquareIcon, ChartBarIcon, CalendarIcon,
    ClockIcon, CheckCircleIcon, ExclamationCircleIcon,
    UserPlusIcon, ArrowPathIcon, EyeIcon
} from "@heroicons/react/24/outline";
import Price from "../../components/common/Price";

import BulkImportControl from '../../components/common/BulkImportControl';
import RoleGate from "../../components/RoleGate";
import { API_BASE_URL } from '../../api/axiosConfig';
import useModuleFilters from "../../hooks/useModuleFilters";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import TableActionButtons from "../../components/common/TableActionButtons";

const WorkforcePage = () => {
  const navigate = useNavigate();
  const { 
    workforce = [], 
    dashboardStats,
    loading,
    deleteWorkforce, 
    allocateToProject,
    fetchWorkforce,
    fetchDashboardStats
  } = useWorkforce();
  
  const { projects = [] } = useProjects();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("list");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAvailability, setFilterAvailability] = useState("");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
    clearFilters,
    clearingFilters,
  } = useModuleFilters("workforce", {
    departments: [],
    statuses: [],
    projects: [],
  });

  const handleClearAll = () => {
    setFilterDepartment("");
    setFilterStatus("");
    setFilterAvailability("");
    setSearchTerm("");
    if (fetchWorkforce) fetchWorkforce();
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [allocationData, setAllocationData] = useState({
      project_id: "", role_on_project: "", allocation_percentage: "100",
      billable: true, billing_rate: "", start_date: "", end_date: ""
  });

  useEffect(() => { 
    if (fetchWorkforce) fetchWorkforce();
    if (fetchDashboardStats) fetchDashboardStats(); 
  }, [fetchWorkforce, fetchDashboardStats]);

  const departments = [...new Set(workforce.map(w => w.department).filter(Boolean))];

  const filtered = workforce.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         p.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = !filterDepartment || p.department === filterDepartment;
    const matchesStatus = !filterStatus || p.status === filterStatus;
    const matchesAvail = !filterAvailability || p.calculated_status === filterAvailability;
    return matchesSearch && matchesDept && matchesStatus && matchesAvail;
  });

  const openAllocateModal = (person) => {
      setSelectedPerson(person);
      setAllocationData({
          project_id: "", role_on_project: person.role, allocation_percentage: "100",
          billable: true, billing_rate: person.hourly_wage || "", start_date: new Date().toISOString().split('T')[0], end_date: ""
      });
      setIsModalOpen(true);
  };

  const handleAllocateSubmit = async (e) => {
      e.preventDefault();
      try {
          await allocateToProject({ workforce_id: selectedPerson.id, ...allocationData });
          setIsModalOpen(false);
      } catch (err) { console.error(err); }
  };

  const handleRefresh = () => {
    fetchWorkforce();
    fetchDashboardStats();
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Available': return 'bg-green-50 text-green-700 border-green-200';
      case 'Partially Assigned': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'Fully Booked': return 'bg-red-50 text-red-700 border-red-200';
      case 'On Leave': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const columns = [
    {
      header: "Employee",
      accessor: "name",
      sortable: true,
      render: (person) => (
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-[11px] shadow-sm">
                {person.name.charAt(0)}
            </div>
            <div>
                <div className="font-bold text-gray-900">{person.name}</div>
                <div className="text-[10px] text-gray-500 font-mono">{person.employee_id}</div>
            </div>
        </div>
      )
    },
    {
      header: "Department",
      accessor: "department",
      sortable: true,
      render: (person) => <span className="px-1.5 py-0.5 bg-gray-50 border border-gray-200 text-gray-700 rounded text-[10px] font-medium">{person.department || 'N/A'}</span>
    },
    {
      header: "Role & Wage",
      accessor: "role",
      sortable: true,
      render: (person) => (
        <div>
            <div className="font-semibold text-gray-800">{person.role}</div>
            <div className="text-[10px] text-gray-500"><Price amount={person.hourly_wage} />/hr · <Price amount={person?.monthly_salary} />/mo</div>
        </div>
      )
    },
    {
      header: "Active Projects",
      render: (person) => (
        <div className="space-y-1">
            {person.allocations && person.allocations.length > 0 ? (
                person.allocations.slice(0, 2).map(alloc => (
                    <div key={alloc.id} className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5 flex items-center gap-1 w-fit">
                        <BriefcaseIcon className="w-3 h-3"/>
                        {alloc.project_name} ({alloc.allocation_percentage}%)
                    </div>
                ))
            ) : (
                <span className="text-gray-400 italic text-[11px]"><T>No projects</T></span>
            )}
        </div>
      )
    },
    {
      header: "Availability",
      accessor: "calculated_status",
      align: "center",
      sortable: true,
      render: (person) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${getStatusColor(person.calculated_status)}`}>
            {person.calculated_status || 'Available'}
        </span>
      )
    },
    {
      header: "Actions",
      align: "right",
      render: (person) => (
        <TableActionButtons
          onView={() => navigate(`/admin/employee/view/${person.id}`)}
          onEdit={() => navigate(`/admin/workforce/edit/${person.id}`)}
          onDelete={() => deleteWorkforce(person.id)}
          extraActions={
            <button
              type="button"
              onClick={() => openAllocateModal(person)}
              title="Allocate Project"
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50/70 dark:hover:bg-indigo-950/40 hover:border-indigo-200 dark:hover:border-indigo-800/60 shadow-2xs transition-colors cursor-pointer"
            >
              <BriefcaseIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
          }
        />
      )
    }
  ];

  const stats = useMemo(() => [
    {
      label: "Total Staff",
      value: dashboardStats?.total_employees || workforce.length || 0,
      icon: UserGroupIcon,
      variant: "default",
      isActive: !filterStatus && !filterAvailability,
      onClick: () => { setFilterStatus(""); setFilterAvailability(""); },
    },
    {
      label: "Active",
      value: dashboardStats?.active_employees || workforce.length || 0,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filterStatus === "Active",
      onClick: () => setFilterStatus(s => s === "Active" ? "" : "Active"),
    },
    {
      label: "Available",
      value: dashboardStats?.available || 0,
      icon: ClockIcon,
      variant: "blue",
      isActive: filterAvailability === "Available",
      onClick: () => setFilterAvailability(a => a === "Available" ? "" : "Available"),
    },
    {
      label: "Fully Booked",
      value: dashboardStats?.fully_booked || 0,
      icon: ExclamationCircleIcon,
      variant: "orange",
      isActive: filterAvailability === "Fully Booked",
      onClick: () => setFilterAvailability(a => a === "Fully Booked" ? "" : "Fully Booked"),
    },
  ], [dashboardStats, workforce, filterStatus, filterAvailability]);

  return (
    <PageLayout>
      {/* ── HEADER ── */}
      <PageHeader
        icon={UserGroupIcon}
        iconColor="text-[#E8612C]"
        title="Workforce & Site Muster"
        subtitle="Human resources, allocations, and GPS geofenced site attendance"
        onRefresh={handleRefresh}
        loading={loading}
      >
        <RoleGate permission="workforce.manage">
          <BulkImportControl 
            tableName="Workforce"
            apiEndpoint={`${API_BASE_URL}/api/workforce/bulk-import`}
            onRefresh={handleRefresh} 
          />
        </RoleGate>
        <TopPrimaryButton
          onClick={() => navigate("/admin/workforce/add")}
          label="Onboard Employee"
        />
      </PageHeader>

      {/* ── GPS ATTENDANCE & MUSTER ROLL PREVIEW ── */}
      <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-4 shadow-xs mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
          {/* Geofence info */}
          <div className="lg:col-span-4 bg-slate-50 dark:bg-[#21262d] border border-slate-200/70 dark:border-[#30363d] rounded-xl p-3.5 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-bold text-slate-900 dark:text-white">Tower B site</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Geofence radius 150 m</p>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60">
                126 / 134 present
              </span>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200/60 dark:border-[#30363d] text-xs text-slate-600 dark:text-slate-400">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>GPS verified clock-ins active</span>
            </div>
          </div>

          {/* Today's Muster Live Ticker */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-2">
            {[
              { initials: "RK", name: "Ravi Kumar", trade: "Bar bender", time: "07:58", dist: "42 m inside" },
              { initials: "SS", name: "Salim Shaikh", trade: "Mason", time: "08:02", dist: "18 m inside" },
              { initials: "JD", name: "Joseph D'Souza", trade: "Welder", time: "08:05", dist: "65 m inside" },
              { initials: "MY", name: "Manoj Yadav", trade: "Carpenter", time: "08:11", dist: "31 m inside" },
            ].map((worker) => (
              <div key={worker.name} className="flex items-center gap-2.5 p-2 bg-slate-50/70 dark:bg-[#21262d]/70 rounded-xl border border-slate-100 dark:border-[#30363d]">
                <div className="w-7 h-7 rounded-lg bg-orange-50 dark:bg-orange-950/30 text-[#D9531E] font-bold text-[11px] flex items-center justify-center shrink-0 border border-orange-200/60 dark:border-orange-800/50">
                  {worker.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{worker.name}</p>
                    <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500">{worker.time}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-slate-500 dark:text-slate-400">{worker.trade}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{worker.dist}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Payroll Batch CTA */}
          <div className="lg:col-span-3 flex flex-col justify-center items-center p-3 bg-slate-900 rounded-xl text-white text-center">
            <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400">Payroll Run</p>
            <p className="text-sm font-bold mt-1">Week 39 Muster Ready</p>
            <p className="text-xs text-slate-400 mt-0.5">134 workers validated</p>
            <button 
              onClick={() => navigate("/admin/reporting-analytics")}
              className="mt-3 w-full py-1.5 px-3 bg-[#D9531E] hover:bg-orange-600 text-white rounded-lg text-xs font-bold transition shadow-xs cursor-pointer"
            >
              Generate Batch →
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI METRICS ROW ── */}
      <StatsGrid stats={stats} cols={4} />

      {/* ── TOOLBAR: SEARCH & FILTERS ── */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search employee by name, ID, or trade..."
        tabs={['All', 'Active', 'On Leave', 'Inactive']}
        activeTab={filterStatus || 'All'}
        onTabChange={(st) => setFilterStatus(st === 'All' ? '' : st)}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      >
        <ViewSwitcher
          view={view}
          onViewChange={setView}
          availableViews={['list', 'kanban']}
        />
      </Toolbar>

      {/* ── FILTER PANEL ── */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        isClearing={clearingFilters}
        onClear={handleClearAll}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Department</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            value={filterDepartment} 
            onChange={e => setFilterDepartment(e.target.value)} 
            disabled={loadingFilters}
            className="w-full h-8 px-2.5 text-xs bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="">All Departments</option>
            {(filterOptions.departments?.length > 0 ? filterOptions.departments : departments).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <T>Availability</T>
          </label>
          <select 
            value={filterAvailability} 
            onChange={e => setFilterAvailability(e.target.value)} 
            className="w-full h-8 px-2.5 text-xs bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="">All Availability</option>
            <option value="Available">Available</option>
            <option value="Partially Assigned">Partially Assigned</option>
            <option value="Fully Booked">Fully Booked</option>
            <option value="On Leave">On Leave</option>
          </select>
        </div>
      </FilterPanel>

      {/* ── TABLE / KANBAN VIEW ── */}
      <TableCard>
        {view === "list" ? (
          <DataTable data={filtered} columns={columns} tableId="workforce" emptyMessage="No employees found." />
        ) : (
          <WorkforceKanbanView workforce={filtered} onAllocateClick={openAllocateModal} />
        )}
      </TableCard>

      {isModalOpen && selectedPerson && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm p-4 animate-fade-in">
             <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-2xl w-full max-w-lg border border-slate-200/80 dark:border-[#30363d] overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-[#30363d] bg-gray-50 dark:bg-[#21262d] rounded-t-xl">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white"><T>Assign Project</T></h3>
                    <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400 rounded transition"><XMarkIcon className="w-5 h-5"/></button>
                </div>
                <form onSubmit={handleAllocateSubmit} className="p-5 space-y-4">
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-300">Allocating: {selectedPerson.name}</p>
                    <select required className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2.5 text-sm bg-white dark:bg-[#0d1117] text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#D9531E]" value={allocationData.project_id} onChange={e => setAllocationData({...allocationData, project_id: e.target.value})}>
                        <option value="">-- Choose Project --</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-[#30363d]">
                        <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-1.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-lg text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition">Cancel</button>
                        <button type="submit" className="px-4 py-1.5 bg-[#D9531E] hover:bg-orange-700 text-white rounded-lg text-sm font-bold shadow-xs transition">Confirm Allocation</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </PageLayout>
  );
}
export default WorkforcePage;